import { useMemo } from 'react';
import { Grid, MenuItem, TextField, Typography } from '@mui/material';
import { toast } from 'sonner';

import { usePatchLoanDemographicsMutation } from '@/apis/extended/loanApplicationApi';
import type { LoanApplication } from '@/types/LoanApplicationType';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { useLoanSectionForm } from './useLoanSectionForm';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
};

type DemoState = {
  ethnicity: string;
  race: string;
  sex: string;
};

const DECLINE = 'I do not wish to provide';

function fromApp(d: Record<string, unknown> | undefined): DemoState {
  const x = d || {};
  return {
    ethnicity: String(x.ethnicity ?? ''),
    race: String(x.race ?? ''),
    sex: String(x.sex ?? ''),
  };
}

export function DemographicsSection({ application, readOnly }: Props) {
  const [patch, { isLoading }] = usePatchLoanDemographicsMutation();

  const initial = useMemo(
    () => fromApp(application.demographics),
    [application._id, application.demographics, application.updatedAt]
  );

  const {
    draft: local,
    setDraft: setLocal,
    isDirty,
    markSaved,
    resetDraft,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: 'demographics',
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: initial,
    readOnly,
  });

  const saveSection = async () => {
    if (formReadOnly) return;
    try {
      await patch({
        id: application._id,
        ethnicity: local.ethnicity || undefined,
        race: local.race || undefined,
        sex: local.sex || undefined,
      }).unwrap();
      markSaved(local);
      toast.success('Saved');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const disabled = formReadOnly || isLoading;

  const sel = (label: string, key: keyof DemoState, options: string[]) => (
    <TextField
      fullWidth
      select
      label={label}
      value={local[key]}
      disabled={disabled}
      onChange={(e) => setLocal((s) => ({ ...s, [key]: e.target.value }))}
    >
      <MenuItem value="">—</MenuItem>
      <MenuItem value={DECLINE}>{DECLINE}</MenuItem>
      {options.map((o) => (
        <MenuItem key={o} value={o}>
          {o}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Federal government monitoring information (URLA Section 8). You may
        decline to furnish this information.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          {sel('Ethnicity', 'ethnicity', [
            'Hispanic or Latino',
            'Not Hispanic or Latino',
          ])}
        </Grid>
        <Grid item xs={12} md={4}>
          {sel('Race', 'race', [
            'American Indian or Alaska Native',
            'Asian',
            'Black or African American',
            'Native Hawaiian or Other Pacific Islander',
            'White',
          ])}
        </Grid>
        <Grid item xs={12} md={4}>
          {sel('Sex', 'sex', ['Female', 'Male'])}
        </Grid>
      </Grid>
      <LoanSectionFormFooter
        readOnly={formReadOnly}
        isDirty={isDirty}
        isSaving={isLoading}
        saveLabel="Save demographics"
        onSave={() => void saveSection()}
        onCancel={resetDraft}
      />
    </div>
  );
}
